import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { HelmetProvider } from 'react-helmet-async';
import { ToastProvider, useToast } from "./components/Toast/Toast";
import { setupAxiosInterceptors } from "./utils/apiInterceptor";
import { showFileAccessDeniedToast } from "./utils/apiErrorHandler";
import { AppStateProvider } from './state';
import { PreferencesStateProvider } from './state/PreferencesStateContext';
import { useEditorPreferences } from './features/settings/hooks/useEditorPreferences';
import { getCachedDarkMode, resolveDarkMode } from './utils/theme';
import './utils/debug.js';
import { analytics } from './services/analyticsService';
import autoEventTracking from './services/autoEventTracking';
import { autoReportCrash } from './services/incidentService';
import { FeatureFlagProvider } from './contexts/FeatureFlagContext';
import PaperFoldingLoader from './components/ui/PaperFoldingLoader';
import CookieConsent from './components/common/CookieConsent';
import NotificationManager from './components/ui/NotificationManager';
import ReportIssueModal from './components/ui/ReportIssueModal';
import AIFeedbackModal from './components/ui/AIFeedbackModal';
import ErrorBoundary from './components/ui/ErrorBoundary';
import syncService from './services/syncService';
import authService from './services/authService';
import apiClient from './services/apiClient';
import { electronAPI, isElectron } from './utils/electronBridge';
import localFileService from './services/localFileService';
import UsageBanner from './components/billing/UsageBanner';
import UpdateBanner from './components/UpdateBanner';
import GlobalSearchModal from './components/search/GlobalSearchModal';
import AssetLibraryDrawer from './features/assets/AssetLibraryDrawer';

// Configure axios to send cookies with all requests (for cookie-based authentication)
axios.defaults.withCredentials = true;

// Lazy load heavy components for better initial load performance
const Screenwriter = lazy(() => import("./ScreenWriter.jsx"));
const PublicView = lazy(() => import("./PublicView.jsx"));
const LoginPage = lazy(() => import("./features/auth/components/LoginPage.jsx"));
const VerifyEmailPage = lazy(() => import("./features/auth/components/VerifyEmailPage.jsx"));
const RegistrationSuccessPage = lazy(() => import("./features/auth/components/RegistrationSuccessPage.jsx"));
const ResetPasswordMagicPage = lazy(() => import("./features/auth/components/ResetPasswordMagicPage.jsx"));
const InviteAcceptModal = lazy(() => import("./components/collaboration/InviteAcceptModal.jsx"));
const OAuthConsentPage = lazy(() => import("./features/oauth/components/OAuthConsentPage.jsx"));
const Dashboard = lazy(() => import("./pages/Dashboard.jsx"));
const AppShell = lazy(() => import("./layouts/AppShell.jsx"));
const ChatSessionPage = lazy(() => import("./pages/ChatSessionPage.jsx"));
const ProjectNotebooksPage = lazy(() => import("./pages/ProjectNotebooksPage.jsx"));
const BoardsHub = lazy(() => import("./pages/BoardsHub.jsx"));
const AssetLibraryPage = lazy(() => import("./pages/AssetLibraryPage.jsx"));
const ProductionSheetPage = lazy(() => import("./pages/ProductionSheetPage.jsx"));
const SettingsModal = lazy(() => import("./features/settings/components/SettingsModal.jsx"));
const TermsAndConditionsPage = lazy(() => import("./features/auth/components/TermsAndConditionsPage.jsx"));
const PrivacyPolicyPage = lazy(() => import("./features/auth/components/PrivacyPolicyPage.jsx"));
const ShareLinkHandler = lazy(() => import("./pages/ShareLinkHandler.jsx"));
const ProtectedRoute = lazy(() => import("./components/auth/ProtectedRoute.jsx"));
const SharedSessionView = lazy(() => import("./components/chat/SharedSessionView.jsx"));
const DiscussionNotesPage = lazy(() => import("./features/notes/pages/DiscussionNotesPage.jsx"));

// Production pages
const NotFoundPage = lazy(() => import("./pages/NotFoundPage.jsx"));

const MaintenancePage = lazy(() => import("./pages/MaintenancePage.jsx"));

// Loading fallback component
const LoadingFallback = () => {
  const [isDarkMode] = useState(getCachedDarkMode);

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: isDarkMode ? '#0a0a0a' : '#ffffff'
    }}>
      <PaperFoldingLoader isDarkMode={isDarkMode} message="Loading..." />
    </div>
  );
};

// Google Drive OAuth Callback Component
// Legacy session URL redirect — a session ID from an old /sessions/:id link
// maps onto that project's chat route (there's no more file sub-route to
// preserve -- session switching is in-page state now, not a URL segment).
function LegacySessionRedirect() {
  const { sessionId } = useParams();
  return <Navigate to={`/projects/${sessionId}/chat`} replace />;
}

// Legacy content-type route redirect -- /screenplay, /book, /shortform,
// /production (with or without a fileId) used to each be their own
// top-level workspace; there's just one /editor now, and a file's own
// template decides which editor UI renders it.
function LegacyModeRedirect() {
  const { fileId } = useParams();
  return <Navigate to={fileId ? `/editor/${fileId}` : '/editor'} replace />;
}

// Google Auth OAuth Callback Component
function GoogleAuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const hasExchanged = React.useRef(false);
  const [handedOffToDesktopApp, setHandedOffToDesktopApp] = React.useState(false);

  useEffect(() => {
    // Prevent double exchange in React strict mode or re-renders
    if (hasExchanged.current) return;

    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    // Set by GET /api/auth/google when authService.loginWithGoogle() (main
    // app) or offload-app's login-google handler was called -- this whole
    // page is running in the system browser (both apps shell any
    // non-app-origin navigation, including the Google consent screen, out to
    // it), so a plain '/' redirect below would just strand the finished
    // session here instead of in the desktop app. `state` also says WHICH
    // desktop app to hand off to, since each registers its own protocol.
    const state = params.get('state');
    const isElectronHandoff = state === 'electron' || state === 'electron-offload' || state === 'electron-companion';

    if (code) {
      hasExchanged.current = true;

      // Exchange code for JWT tokens
      const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:3002';

      axios.post(`${serverUrl}/api/auth/google/exchange`, { code, platform: isElectronHandoff ? state : undefined }, {
          withCredentials: true // Important: Send/receive cookies
        })
        .then((response) => {
          const { user, electronHandoffCode } = response.data;

          if (electronHandoffCode) {
            // Hand the finished session back to the desktop app via its
            // registered custom protocol instead of navigating this browser
            // tab anywhere -- that app's protocol handler exchanges this
            // one-time code for the real tokens itself. Each desktop app
            // registers its own scheme so the installs don't collide.
            const scheme = state === 'electron-offload' ? 'tarr-offload'
              : state === 'electron-companion' ? 'tarr-companion'
              : 'tarr';
            setHandedOffToDesktopApp(true);
            window.location.href = `${scheme}://auth-callback?code=${encodeURIComponent(electronHandoffCode)}`;
            return;
          }

          // Tokens are now in httpOnly cookies (set by server)
          // Only store user data in localStorage
          localStorage.setItem('currentUser', JSON.stringify(user));

          // Set user ID for analytics
          if (user?.id) {
            analytics.setUserId(user.id);
          }

          // Redirect to home - page will reload and authService will pick up from cookies
          window.location.href = '/';
        })
        .catch((error) => {
          console.error('Failed to exchange Google auth code:', error);
          // /google/exchange 403s for a brand-new Google account with no
          // pending invite for that email (see InviteCodeService) — surface
          // that specific message on the login page instead of the generic
          // toast, so the user knows they need an invite rather than just
          // seeing an unexplained failure.
          if (error.response?.status === 403) {
            navigate('/login', { replace: true, state: { googleInviteRequired: error.response?.data?.message || 'This email doesn\'t have a pending invite. Please request access before signing up.' } });
            return;
          }
          if (toast) {
            toast.error('Failed to sign in with Google');
          }
          navigate('/login', { replace: true });
        });
    } else {
      navigate('/login', { replace: true });
    }
  }, [location, navigate, toast]);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <div>
        <h2>{handedOffToDesktopApp ? 'Signed in!' : 'Signing in with Google...'}</h2>
        <p>{handedOffToDesktopApp
          ? 'You can close this tab and return to the TARR desktop app.'
          : 'Please wait while we complete the authentication.'}</p>
      </div>
    </div>
  );
}

// Component to setup global axios interceptors
function ApiSetup({ children }) {
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    // Setup interceptors on the global axios instance
    // This will intercept ALL axios calls throughout the app
    setupAxiosInterceptors(axios, toast, navigate);
  }, [toast, navigate]);

  return children;
}


function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  // Theme comes from the shared `isDarkMode` preference (follows the user across
  // devices; undefined = follow the OS) -- the same value UIStateContext reads, so
  // the app shell and the editor can no longer disagree.
  const { preferences: themePreferences, updatePreference: updateThemePreference } = useEditorPreferences();
  const isDarkMode = resolveDarkMode(themePreferences.isDarkMode);

  const [currentUser, setCurrentUser] = useState(() => {
    const userStr = localStorage.getItem('currentUser');
    try {
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  });

  const [showReportModal, setShowReportModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [initialSettingsPanel, setInitialSettingsPanel] = useState('preferences');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showAssetLibrary, setShowAssetLibrary] = useState(false);
  const [assetLibraryProjectId, setAssetLibraryProjectId] = useState(null);
  const [inviteToken, setInviteToken] = useState(null);

  // Cmd/Ctrl+K opens the global command-palette search from anywhere,
  // authenticated routes only (matches window.__open*Modal's currentUser gating).
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setShowSearchModal(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Expose report modal trigger globally so any component can open it
  useEffect(() => {
    window.__openReportIssueModal = () => setShowReportModal(true);
    window.__openAIFeedbackModal = () => setShowFeedbackModal(true);
    window.__openGlobalSearch = () => setShowSearchModal(true);
    // Asset Library drawer -- openable from anywhere (any page, regardless
    // of route) the same way Settings is. Callers (e.g. MainSidebar's
    // ProjectHeaderMenu) pass the projectId whose assets to show.
    window.__openAssetLibrary = (projectId) => {
      setAssetLibraryProjectId(projectId || null);
      setShowAssetLibrary(true);
    };
    // Settings is now a modal overlay, not a route -- ActivityBar's gear
    // icon (and anything else that used to navigate('/settings')) calls
    // this instead, so the page underneath stays mounted rather than being
    // unmounted by a route change. See the /settings route below for the
    // deep-link path (direct URL, refresh, apiErrorHandler.js's
    // navigate('/settings#subscription')), which still works unchanged.
    window.__openSettingsModal = (panel) => {
      setInitialSettingsPanel(panel || 'preferences');
      setShowSettingsModal(true);
    };
    return () => {
      delete window.__openReportIssueModal;
      delete window.__openAIFeedbackModal;
      delete window.__openSettingsModal;
      delete window.__openGlobalSearch;
      delete window.__openAssetLibrary;
    };
  }, []);

  // Deep-link support: landing on /settings (or /settings#tab) directly --
  // typed URL, refresh, a bookmark, or an in-app navigate('/settings#...')
  // call (e.g. apiErrorHandler.js's rate-limit/upgrade toasts) -- opens the
  // same modal instead of rendering a dedicated full-page route, then bounces
  // straight back to the sessions hub so something real sits behind it.
  useEffect(() => {
    if (location.pathname !== '/settings') return;
    const hash = location.hash.replace('#', '');
    setInitialSettingsPanel(hash || 'preferences');
    setShowSettingsModal(true);
    navigate('/sessions', { replace: true });
  }, [location.pathname, location.hash, navigate]);

  // Deep-link support: an emailed invite link (/collaboration/invite/:token
  // for a file share, /accept-invite/:token or /accept-invitation/:token for
  // an organization invite) used to render its own full-page component
  // (InviteAcceptHandler.jsx) -- same fix as /settings above: this opens a
  // modal instead and bounces to /sessions so the real app renders behind
  // it, rather than replacing the whole screen with an isolated page.
  // Organization invites still auto-accept silently (unchanged); a file
  // invite opens InviteAcceptModal's confirm/decline popup.
  useEffect(() => {
    const path = location.pathname;
    const fileMatch = path.match(/^\/collaboration\/invite\/([^/]+)/);
    const orgMatch = path.match(/^\/accept-invit(?:e|ation)\/([^/]+)/);
    if (!fileMatch && !orgMatch) return;

    if (!localStorage.getItem('currentUser')) {
      window.location.href = `/login?redirect=${encodeURIComponent(path + location.search)}`;
      return;
    }

    if (fileMatch) {
      setInviteToken(fileMatch[1]);
      navigate('/sessions', { replace: true });
      return;
    }

    // Organization invite: silent accept, same as before.
    const token = orgMatch[1];
    (async () => {
      try {
        await apiClient.post(`/api/invitations/${token}/accept`);
        toast.success('Invitation accepted');
      } catch (err) {
        if (/already/i.test(err.message || '')) {
          toast.success("You're already a member of this organization");
        } else {
          toast.error(err.message || 'Failed to accept invitation');
        }
      }
    })();
    navigate('/', { replace: true });
  }, [location.pathname, location.search, navigate, toast]);

  // Handle Google Auth callback
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get('googleAuth') === 'success') {
      const token = params.get('token');
      const refreshToken = params.get('refreshToken');

      if (token && refreshToken) {
        // Store tokens
        localStorage.setItem('authToken', token);
        localStorage.setItem('refreshToken', refreshToken);

        // Clean up URL
        navigate('/', { replace: true });

        // Show success message
        if (toast) {
          toast.success('Successfully logged in with Google!');
        }
      }
    }
  }, [location, navigate, toast]);

  // Finish the Electron Google sign-in handoff -- the actual token exchange
  // happened in electron/main.js (it owns the httpOnly cookies), which fires
  // this event once that's done. All that's left on this side is the same
  // thing GoogleAuthCallback does for the normal web flow: cache the user
  // and reload so the rest of the app (and its localStorage-only auth check,
  // see ProtectedRoute) picks it up.
  useEffect(() => {
    const unsubscribe = electronAPI.auth.onSignedIn((user) => {
      localStorage.setItem('currentUser', JSON.stringify(user));
      if (user?.id) analytics.setUserId(user.id);
      window.location.href = '/';
    });
    return unsubscribe;
  }, []);

  // Global crash auto-reporting (sends INC email even without user clicking Report)
  useEffect(() => {
    const handleGlobalError = (event) => {
      autoReportCrash(event.error || new Error(event.message));
    };
    const handleUnhandledRejection = (event) => {
      autoReportCrash(event.reason instanceof Error ? event.reason : new Error(String(event.reason)));
    };
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Initialize analytics and auto-tracking
  useEffect(() => {
    // Initialize automatic event tracking once
    if (!window.__AUTO_TRACKING_INITIALIZED__) {
      autoEventTracking.initialize();
      window.__AUTO_TRACKING_INITIALIZED__ = true;
    }

    // Set user ID if user is already logged in
    const currentUserStr = localStorage.getItem('currentUser');
    if (currentUserStr) {
      try {
        const user = JSON.parse(currentUserStr);
        if (user?.id) {
          analytics.setUserId(user.id);
          setCurrentUser(user);
        }
      } catch (error) {
        console.error('Failed to parse currentUser from localStorage:', error);
      }
    }
  }, []);

  // Initialize sync service at app level (runs once on mount)
  useEffect(() => {
    const initSync = async () => {
      try {
        const user = authService.getCurrentUser();
        if (user?.id) {
          console.log('App: Initializing syncService for user:', user.id);
          await syncService.initialize();
          console.log('App: SyncService initialized successfully');

          // Electron only: after the first pull from the server, ask once
          // where to keep local copies (defaults to Documents) and write
          // everything already pulled into it -- the hybrid cloud + local
          // setup. No-op on web, when a folder is already set, or once asked.
          if (isElectron) {
            const folder = await localFileService.promptForProjectFolderIfNeeded();
            if (folder) await syncService.mirrorAllToLocal();
          }
        } else {
          console.log('App: No authenticated user, skipping sync initialization');
        }
      } catch (error) {
        console.error('App: Failed to initialize syncService:', error);
      }
    };

    initSync();
  }, []);

  // Listen for user authentication events
  useEffect(() => {
    const handleUserAuth = (event) => {
      const { user } = event.detail || {};
      if (user) {
        setCurrentUser(user);
      }
    };

    const handleLogout = () => {
      setCurrentUser(null);
    };

    // apiClient.js's own axios instance (the one most of the app's real API
    // calls actually go through, separate from setupAxiosInterceptors'
    // global `axios` object above) can't show a toast itself -- it
    // dispatches this instead so the "Request Access" action can be shown
    // from here, where toast is actually in scope.
    const handleFileAccessDenied = (event) => {
      if (event.detail) showFileAccessDeniedToast(event.detail, toast);
    };

    document.addEventListener('userAuthenticated', handleUserAuth);
    document.addEventListener('userLoggedOut', handleLogout);
    document.addEventListener('fileAccessDenied', handleFileAccessDenied);

    return () => {
      document.removeEventListener('userAuthenticated', handleUserAuth);
      document.removeEventListener('userLoggedOut', handleLogout);
      document.removeEventListener('fileAccessDenied', handleFileAccessDenied);
    };
  }, [toast]);

  // Theme toggle handler
  const toggleTheme = () => updateThemePreference('isDarkMode', !isDarkMode);

  return (
    <ApiSetup>
      <FeatureFlagProvider user={currentUser}>
        <AppStateProvider>
          <Suspense fallback={<LoadingFallback />}>
            {/* Update Banner - shown in Electron when updates are available */}
            <UpdateBanner isDarkMode={isDarkMode} />

            {/* Usage Banner - shown for authenticated users above main content */}
            {currentUser && <UsageBanner isDarkMode={isDarkMode} />}

            <Routes>
              {/* Public routes - no authentication required */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/registration-success" element={<RegistrationSuccessPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/auth/reset-password-magic" element={<ResetPasswordMagicPage />} />
              <Route path="/auth/magic" element={<ResetPasswordMagicPage />} />
              {/* Deep-link only -- the effect above opens InviteAcceptModal
                  (file invites) or silently accepts (org invites) and
                  immediately replaces this with /sessions or /, so these
                  elements only ever render for a single frame. */}
              <Route path="/accept-invite/:token" element={<div style={{ width: '100%', height: '100vh', backgroundColor: isDarkMode ? '#0a0a0a' : '#ffffff' }} />} />
              <Route path="/accept-invitation/:token" element={<div style={{ width: '100%', height: '100vh', backgroundColor: isDarkMode ? '#0a0a0a' : '#ffffff' }} />} />
              <Route path="/collaboration/invite/:token" element={<div style={{ width: '100%', height: '100vh', backgroundColor: isDarkMode ? '#0a0a0a' : '#ffffff' }} />} />
              <Route path="/share/invite/:token" element={<ShareLinkHandler />} />
              <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
              {/* OAuth consent screen for the "connect tarr.ink to Claude" flow
                  (server/routes/oauth.js's GET /oauth/authorize redirects here
                  with ?request_id=... once the browser has a valid session).
                  Not wrapped in ProtectedRoute -- the page handles its own
                  defensive 401 -> /login?returnTo=... redirect so it can send
                  the user straight back to this exact consent URL afterward. */}
              <Route path="/oauth/consent" element={<OAuthConsentPage isDarkMode={isDarkMode} />} />
              <Route path="/public" element={<PublicView />} />
              <Route path="/share/:shareId" element={<PublicView />} />
              <Route path="/chat/shared/:shareToken" element={<SharedSessionView />} />
              <Route path="/terms" element={<TermsAndConditionsPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/maintenance" element={<MaintenancePage isDarkMode={isDarkMode} />} />

              {/* Protected routes - require authentication */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard isDarkMode={isDarkMode} />
                </ProtectedRoute>
              } />
              {/* Legacy session links map onto project chat */}
              <Route path="/sessions/:sessionId" element={
                <ProtectedRoute>
                  <LegacySessionRedirect />
                </ProtectedRoute>
              } />
              <Route path="/sessions/:sessionId/files/:fileId" element={
                <ProtectedRoute>
                  <LegacySessionRedirect />
                </ProtectedRoute>
              } />

              {/* One workspace: Chat + Editor, sharing one persistent shell
                  (AppShell -- MainSidebar's own Chat/Editor tab is the only
                  top-level nav now). A pathless layout route so its children
                  don't need a common path prefix -- switching between any of
                  them is a real route change that never remounts the shell. */}
              <Route element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }>
                {/* NotebookLM-style home: card view of projects. One project =
                    one notebook = one chat — the old flat session list and
                    "new random session" redirect are gone. */}
                <Route path="/" element={<ProjectNotebooksPage />} />
                <Route path="/sessions" element={<ProjectNotebooksPage />} />
                <Route path="/projects/:projectId/assets" element={<AssetLibraryPage />} />
                {/* One route with an optional :sessionId, not two -- two separate
                    Route elements made React unmount and remount the whole chat
                    page (spinner + full re-init) on the bare-project ->
                    /chat/:sessionId redirect and on every session switch. */}
                <Route path="/projects/:projectId/chat/:sessionId?" element={<ChatSessionPage />} />
                <Route path="/editor" element={<Screenwriter />} />
                <Route path="/editor/:fileId" element={<Screenwriter />} />
                <Route path="/editor/:fileId/:board" element={<Screenwriter />} />
                <Route path="/discussions/:fileId" element={<DiscussionNotesPage />} />
                <Route path="/discussions/:fileId/:discussionId" element={<DiscussionNotesPage />} />
                {/* Storyboard/Beatsheet standalone module -- reachable from
                    MainSidebar's "Boards" entry regardless of whether the
                    user is currently in Chat or Editor, instead of only
                    opening as an editor-toolbar-triggered overlay. */}
                <Route path="/boards" element={<BoardsHub />} />
                <Route path="/boards/:fileId" element={<BoardsHub />} />
                <Route path="/boards/:fileId/:boardId" element={<BoardsHub />} />
                {/* Production sheets as their own dedicated page (own header,
                    no editor-toolbar clutter) -- reachable from MainSidebar's
                    "Sheets" entry, same standalone-route pattern as Boards.
                    ScreenWriter.jsx no longer renders ProductionWorkspace at
                    all; a stale /editor/:fileId link for a sheet redirects
                    here instead. */}
                <Route path="/sheets" element={<ProductionSheetPage />} />
                <Route path="/sheets/:fileId" element={<ProductionSheetPage />} />
              </Route>

              {/* Deep-link only -- the effect above opens SettingsModal and
                  immediately replaces this with /sessions, so this element
                  only ever renders for a single frame. Kept minimal (no
                  panel logic) rather than importing the old full-page
                  Settings component just for that one frame. */}
              <Route path="/settings" element={
                <ProtectedRoute>
                  <div style={{ width: '100%', height: '100vh', backgroundColor: isDarkMode ? '#0a0a0a' : '#ffffff' }} />
                </ProtectedRoute>
              } />
              {/* Legacy content-type routes -- /screenplay, /book, /shortform,
                  /production are gone as top-level workspaces; redirect any
                  stray bookmark/deep-link into the one /editor route. */}
              {['screenplay', 'book', 'shortform', 'production'].map((legacyMode) => (
                <React.Fragment key={legacyMode}>
                  <Route path={`/${legacyMode}`} element={
                    <ProtectedRoute><LegacyModeRedirect /></ProtectedRoute>
                  } />
                  <Route path={`/${legacyMode}/:fileId`} element={
                    <ProtectedRoute><LegacyModeRedirect /></ProtectedRoute>
                  } />
                  <Route path={`/${legacyMode}/:fileId/:board`} element={
                    <ProtectedRoute><LegacyModeRedirect /></ProtectedRoute>
                  } />
                </React.Fragment>
              ))}

              {/* 404 Catch-all - Must be last */}
              <Route path="*" element={<NotFoundPage isDarkMode={isDarkMode} />} />
            </Routes>

            {/* Notification Manager - Polls for collaboration invitations */}
            <NotificationManager />

            {/* Cookie Consent Banner */}
            <CookieConsent isDarkMode={isDarkMode} />

            {/* Report Issue Modal - openable from anywhere via window.__openReportIssueModal() */}
            <ReportIssueModal isOpen={showReportModal} onClose={() => setShowReportModal(false)} />

            {/* AI Feedback Modal - openable from anywhere via window.__openAIFeedbackModal() */}
            <AIFeedbackModal isOpen={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} />

            {/* Settings Modal - openable from anywhere via window.__openSettingsModal(panel);
                also opened by the /settings deep-link effect above */}
            <SettingsModal
              isOpen={showSettingsModal}
              onClose={() => setShowSettingsModal(false)}
              initialPanel={initialSettingsPanel}
              isDarkMode={isDarkMode}
            />

            {/* Invite Accept Modal -- opened by the /collaboration/invite/:token
                deep-link effect above, a confirm/decline popup instead of a
                dedicated full-page route. */}
            <InviteAcceptModal
              isOpen={!!inviteToken}
              token={inviteToken}
              onClose={() => setInviteToken(null)}
              isDarkMode={isDarkMode}
            />

            {/* Command-palette search (Cmd/Ctrl+K) -- openable from anywhere via
                window.__openGlobalSearch(); only meaningful once authenticated. */}
            {currentUser && (
              <GlobalSearchModal
                isOpen={showSearchModal}
                onClose={() => setShowSearchModal(false)}
                isDarkMode={isDarkMode}
              />
            )}

            {/* Asset Library drawer -- openable from anywhere via
                window.__openAssetLibrary(projectId); also reachable via the
                /projects/:projectId/assets deep-link route (AssetLibraryPage.jsx).
                Reads isDarkMode from useUIState() itself, not passed here --
                see AssetLibraryDrawer.jsx's own docstring for why. */}
            <AssetLibraryDrawer
              isOpen={showAssetLibrary}
              onClose={() => setShowAssetLibrary(false)}
              projectId={assetLibraryProjectId}
            />
          </Suspense>
        </AppStateProvider>
      </FeatureFlagProvider>
    </ApiSetup>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router>
          <PreferencesStateProvider>
            <ToastProvider>
              <AppContent />
            </ToastProvider>
          </PreferencesStateProvider>
        </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
}
